import * as React from "react";
import cn from "classnames";
import Textarea from "react-textarea-autosize";
import { useField } from "formik";
import styles from "./styles.module.scss";

interface IProps {
    name: string;
    label: string;
    placeholder: string;
    showValidationErrorWhenNotTouched?: boolean;
    required?: boolean;
    disabled?: boolean;
    type?: "text" | "date" | "text-area" | "password" | "email";
    className?: string;
    inputClassName?: string;
    "data-testid"?: string;
    helpTooltip?: React.ReactNode;
    labelChildren?: React.ReactNode;
}

function _TextInput(
    {
        name,
        label,
        placeholder,
        showValidationErrorWhenNotTouched = false,
        required = false,
        disabled = false,
        type = "text",
        className,
        inputClassName,
        "data-testid": dataTestId,
        helpTooltip = null,
        labelChildren = null,
    }: IProps,
    ref?: React.ForwardedRef<HTMLInputElement | HTMLTextAreaElement> | null,
): JSX.Element {
    const [field, meta] = useField<string>(name);
    const hasError = Boolean(
        meta.error && (showValidationErrorWhenNotTouched || meta.touched),
    );

    return (
        <label
            className={cn([
                styles.wrapper,
                hasError && styles.hasError,
                className && className,
            ])}
        >
            <div className={styles.labelAndErrorWrapper}>
                <div className={styles.label}>
                    {label}
                    {required && (
                        <span className={styles.requiredAsterisk}>*</span>
                    )}
                    {helpTooltip}
                    {labelChildren}
                </div>
                {hasError && <div className={styles.error}>{meta.error}</div>}
            </div>
            {(type === "text" ||
                type === "date" ||
                type === "password" ||
                type === "email") && (
                <input
                    {...field}
                    placeholder={placeholder}
                    disabled={disabled}
                    type={type}
                    ref={ref as React.ForwardedRef<HTMLInputElement>}
                    className={cn([
                        styles.input,
                        inputClassName && inputClassName,
                    ])}
                    data-testid={dataTestId}
                />
            )}
            {type === "text-area" && (
                <Textarea
                    {...field}
                    placeholder={placeholder}
                    disabled={disabled}
                    ref={ref as React.ForwardedRef<HTMLTextAreaElement>}
                    className={cn([
                        styles.input,
                        inputClassName && inputClassName,
                    ])}
                    data-testid={dataTestId}
                />
            )}
        </label>
    );
}

const TextInputWithRef = React.forwardRef(_TextInput);

export { TextInputWithRef as TextInput };
